// eslint-disable react-hooks/exhaustive-deps
import { Box, CircularProgress } from '@mui/joy'
import { GluonEnvironment } from '@numeo/types-core'
import axios from 'axios'
import React, { createContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import setupAxios, { AUTH_TOKEN, getStoredEnvironment } from 'setup/SetupAxios'
import { AuthContextType } from './types'

const AuthContext = createContext<AuthContextType>({
    verifyAuth: () => Promise.resolve(),
    isAuthenticated: false,
    googleSignIn: () => Promise.resolve(),
    logout: () => {},
})

export const useAuth = () => React.useContext(AuthContext)

interface AuthProviderProps {
    children: React.ReactNode
}

let token: string | null = localStorage.getItem(AUTH_TOKEN)!

const publicPaths = ['/live-test-ai-call']

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [loading, setLoading] = useState<boolean>(true)
    const navigate = useNavigate()
    const [authData, setAuthData] = useState('')

    // Check token validity and get user data
    const verifyAuth = async () => {
        if (publicPaths.includes(window.location.pathname)) {
            setLoading(false)
            return
        }
        if (!token) {
            setLoading(false)
            logout()
            return
        }

        // Initialize axios with environment derived from token (fallback to stored)
        const environment = getStoredEnvironment()
        setupAxios(logout, environment)

        setLoading(false)
        setAuthData(token)
    }

    // Check auth status when token changes
    useEffect(() => {
        verifyAuth()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Google sign-in function
    const googleSignIn = async (credential: string, environment?: GluonEnvironment) => {
        const selectedEnvironment = environment || GluonEnvironment.DEVELOPMENT

        // Setup axios with the selected environment
        setupAxios(logout, selectedEnvironment)

        const response = await axios.post('/gluon/auth/google-sign-in', {
            credential,
            environment: selectedEnvironment,
        })
        const { token: newToken } = response.data
        token = newToken
        localStorage.setItem(AUTH_TOKEN, token!)
        localStorage.setItem('selected_environment', selectedEnvironment)
        setAuthData(token!)
    }

    // Logout function
    const logout = () => {
        localStorage.removeItem(AUTH_TOKEN)
        localStorage.removeItem('auth_data')
        token = null
        setAuthData('')
        navigate('/auth/sign-in')
    }

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <AuthContext.Provider
            value={{
                verifyAuth,
                isAuthenticated: Boolean(authData),
                googleSignIn,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
